import { Document, DocumentBatch } from '../types/Document';
import { AVAILABLE_FOLDERS, createDocument } from '../utils/documentUtils';
import { generateDocumentIds } from '../data/documentRanges';

// Since we're in a static environment, we use pre-generated document lists
// These lists are based on actual files after deduplication, so they contain gaps

// Generate document list based on actual file IDs (not sequential)
const generateDocumentList = (folder: string): Document[] => {
  const documentIds = generateDocumentIds(folder);
  const documents: Document[] = [];
  
  for (const docId of documentIds) {
    const filename = `HOUSE_OVERSIGHT_${docId}`;
    documents.push(createDocument(`${filename}.txt`, folder));
  }
  
  return documents;
};

export const documentService = {
  // Get all available document batches
  async getDocumentBatches(): Promise<DocumentBatch[]> {
    const batches: DocumentBatch[] = [];
    
    for (const folder of AVAILABLE_FOLDERS) {
      const documents = generateDocumentList(folder);
      batches.push({
        batchNumber: folder,
        documents
      });
    }
    
    return batches;
  },

  // Get documents for a specific folder
  async getDocumentsByFolder(folder: string): Promise<Document[]> {
    if (!AVAILABLE_FOLDERS.includes(folder)) {
      throw new Error(`Invalid folder: ${folder}`);
    }
    
    return generateDocumentList(folder);
  },

  // Get a specific document by folder and ID
  async getDocumentByFolderAndId(folderId: string, documentId: string): Promise<Document | null> {
    if (!AVAILABLE_FOLDERS.includes(folderId)) {
      throw new Error(`Invalid folder: ${folderId}`);
    }

    const filename = `HOUSE_OVERSIGHT_${documentId.padStart(6, '0')}`;
    
    try {
      // Create the document object
      const document = createDocument(`${filename}.txt`, folderId);
      
      // Try to load the text content
      const response = await fetch(document.textPath);
      if (response.ok) {
        document.extractedText = await response.text();
      } else {
        document.extractedText = 'Text content not available.';
      }
      
      return document;
    } catch (error) {
      console.error(`Error loading document ${documentId} from folder ${folderId}:`, error);
      return null;
    }
  },

  // Check if document image exists
  async checkDocumentImage(imagePath: string): Promise<boolean> {
    try {
      const response = await fetch(imagePath, { method: 'HEAD' });
      return response.ok;
    } catch {
      return false;
    }
  },

  // Search documents by text content (basic implementation)
  async searchDocuments(query: string, folder?: string): Promise<Document[]> {
    const searchFolders = folder ? [folder] : AVAILABLE_FOLDERS;
    const results: Document[] = [];
    
    for (const searchFolder of searchFolders) {
      const documents = generateDocumentList(searchFolder);
      
      // Filter documents that might contain the query (simulation)
      const filtered = documents.filter((doc: Document) => 
        doc.filename.toLowerCase().includes(query.toLowerCase()) ||
        doc.id.includes(query)
      );
      
      results.push(...filtered.slice(0, 50)); // Limit search results to 50 per folder
    }
    
    return results;
  }
};